import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { State } from 'model/State';
import {
  selectAccountHistoryAsPartner,
  selectGetCustomerByEmail,
  selectSwitchedUser,
} from 'modules/mac/duck/selectors';
import { Dimensions } from 'components/visuals';
import {
  StyledFlex,
  StyledSenInfoWrapper,
  StyledSpan,
  StyledTableData,
  StyledTDSection,
  StyledTextLabel,
  StyledTextValueWrapper,
  TextAreaWrapper,
} from '../licensesExpandedRow/StyledComponents';
import { messages } from '../../messages';
import { OrganisationData } from 'modules/mac/common/types';
import t from 'modules/i18n/intl';
import { SubscriptionHasBeenMovedMessage } from './SubscriptionHasBeenMovedMessage';
import { CloudMigrationTrialOfferBanner } from './CloudMigrationTrialOfferBanner';
import Tooltip from '../../../../components/common/Tooltip';
import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle';
import { colors } from '@atlaskit/theme';
import TextArea from '@atlaskit/textarea';
import AccountHistoryAsPartner from './AccountHistoryAsPartner';
import { isAdvisoryServicesProduct, isSupportOnlyProductKey } from '../../../../util/mac/helpers';
import ActionsSection from '../licensesExpandedRow/ActionsSection';
import {
  StyledParagraphWithTextAlign,
  StyledRedSpan,
  StyledSpan as StyledSpanWithPadding,
} from '../styledComponents';

const InactiveLabel = () => (
  <StyledRedSpan $marginLeft={0.5}>
    <FormattedMessage {...messages.inactive} />
    <Tooltip direction="up-middle" content={<FormattedMessage {...messages.inactiveTooltip} />}>
      <QuestionCircleIcon size="small" primaryColor={colors.N80} label="inactive-tooltip" />
    </Tooltip>
  </StyledRedSpan>
);

export const DetailsRow = ({ label, value, dataCy = '', title = '' }) => {
  return (
    <StyledFlex {...(dataCy ? { 'data-cy': dataCy } : {})} width={`${Dimensions.units(12.5)}%`}>
      <StyledTextLabel title={title}>{label}</StyledTextLabel>
      <StyledTextValueWrapper>
        <StyledSpan $padding={`0 ${Dimensions.units(1.25)}px`}>{value}</StyledSpan>
      </StyledTextValueWrapper>
    </StyledFlex>
  );
};

type PropTypes = {
  accountDetails: any;
  canUserSeeAccountDetails: boolean;
  technicalContacts: OrganisationData[];
};

const DetailsRowContent: React.FC<PropTypes> = ({
  accountDetails,
  canUserSeeAccountDetails,
  technicalContacts,
}) => {
  const userEmail = useSelector(selectSwitchedUser);
  const customerByEmail = useSelector((state) =>
    selectGetCustomerByEmail(state as State)?.[userEmail]?.ifSuccess((r) => r),
  );
  const customerPartnerId = customerByEmail?.organisationDetails?.id;

  const accountHistoryAsPartner: any = useSelector((state) =>
    selectAccountHistoryAsPartner(state as State, accountDetails.id),
  );

  const { latestPeriod, merchandise } = accountDetails;
  const productPackage = merchandise?.productPackage;
  const { key } = productPackage;

  const shouldShowLicenses =
    latestPeriod?.license && !isSupportOnlyProductKey(key) && !isAdvisoryServicesProduct(key);

  if (!accountDetails) return null;
  const isActivePartner = customerPartnerId === accountDetails.latestPeriod.partnerId;

  return canUserSeeAccountDetails ? (
    <>
      {accountDetails.entSubMigrated ? (
        <StyledTableData $paddingLeft colSpan={6}>
          <SubscriptionHasBeenMovedMessage
            transactionAccountId={accountDetails.transactionAccountId}
            externalUuid={accountDetails.externalUuid}
          />
          <StyledTDSection>
            <br />
            <AccountHistoryAsPartner
              accountHistoryAsPartner={accountHistoryAsPartner}
              userPartnerId={customerPartnerId}
              isMigrated={accountDetails.entSubMigrated}
            />
          </StyledTDSection>
        </StyledTableData>
      ) : (
        <>
          <StyledTableData $paddingLeft colSpan={4}>
            <StyledTDSection>
              <StyledSenInfoWrapper>
                <DetailsRow
                  title={t('change.search.label.sen.title')}
                  label={<FormattedMessage {...messages.name} />}
                  value={accountDetails?.name}
                />
                <DetailsRow
                  label={
                    <>
                      <FormattedMessage {...messages.SEN} />
                      <Tooltip
                        direction="up-middle"
                        content={
                          <StyledParagraphWithTextAlign $textAlign="center">
                            <FormattedMessage {...messages.senTooltip} />
                          </StyledParagraphWithTextAlign>
                        }
                      >
                        <StyledSpanWithPadding $margin={`${Dimensions.units(0.5)}px}`}>
                          <QuestionCircleIcon
                            size="small"
                            primaryColor={colors.N80}
                            label="sen-tooltip"
                          />
                        </StyledSpanWithPadding>
                      </Tooltip>
                    </>
                  }
                  value={accountDetails?.id ? `SEN-${accountDetails.id}` : ''}
                  dataCy="mac-sen-details"
                />
                <DetailsRow
                  label={<FormattedMessage {...messages.technicalContact} />}
                  value={
                    <>
                      {technicalContacts.map((technicalContact) => (
                        <p key={technicalContact?.contactDetails?.id}>
                          {technicalContact?.contactDetails.displayName}{' '}
                          {!isActivePartner && <InactiveLabel />}
                          <br />
                          {technicalContact?.contactDetails.phone}
                          <br />
                          {technicalContact?.contactDetails.email}
                          <br />
                          {technicalContact?.organisationDetails.name}
                        </p>
                      ))}
                    </>
                  }
                  dataCy="mac-sen-details"
                />

                {!isActivePartner && <>{t('mac.licenses.inactive.product')}</>}
              </StyledSenInfoWrapper>
            </StyledTDSection>
          </StyledTableData>
          <StyledTableData colSpan={2}>
            <StyledTDSection>
              {!accountDetails.entSubMigrated && (
                <>
                  {shouldShowLicenses && (
                    <>
                      {isActivePartner && (
                        <>
                          <StyledFlex>
                            <StyledTextLabel>
                              <FormattedMessage {...messages.organization} />
                            </StyledTextLabel>
                            <StyledSpan>
                              {latestPeriod?.license?.licenseDetails?.organisation}
                            </StyledSpan>
                          </StyledFlex>
                          <StyledFlex>
                            <StyledTextLabel>
                              <FormattedMessage {...messages.licenseKey} />
                            </StyledTextLabel>
                            <TextAreaWrapper>
                              <TextArea
                                readOnly
                                maxHeight="20vh"
                                rows={5}
                                value={latestPeriod?.license?.licenseDetails?.license}
                                onClick={(e) => {
                                  e.currentTarget.select();
                                }}
                              />
                            </TextAreaWrapper>
                          </StyledFlex>
                        </>
                      )}
                    </>
                  )}
                  <ActionsSection
                    isAccount
                    isEndUserLicenses
                    accountDetails={accountDetails}
                    isActivePartner={isActivePartner}
                  />
                </>
              )}
              <StyledFlex>
                <StyledTextLabel width={`${Dimensions.units(3.4)}%`}>
                  <FormattedMessage {...messages.history} />
                </StyledTextLabel>
                <AccountHistoryAsPartner
                  accountHistoryAsPartner={accountHistoryAsPartner}
                  userPartnerId={customerPartnerId}
                />
              </StyledFlex>
              <CloudMigrationTrialOfferBanner account={accountDetails} />
            </StyledTDSection>
          </StyledTableData>
        </>
      )}
    </>
  ) : (
    // Show the proper error message once we have it: https://hello.jira.atlassian.cloud/browse/CHT-2819
    <StyledTableData $paddingLeft colSpan={6}>
      <p>You don&apos;t have permissions to see the details</p>
    </StyledTableData>
  );
};

export default DetailsRowContent;
